"use client";

import type { CheckboxValueType } from "antd/lib/checkbox/Group";
import type { ReactElement } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { createContext } from "@/helpers/createContext";
import useAxiosQuery from "@/hooks/useMyQuery";
import useMyMutation from "@/hooks/userMyMutation";
import { IStatisticNotification } from "@/models/notification";

export interface NotificationModel {
  _id?: string;
  user_id?: string;
  title?: string;
  description?: string;
  type?: string;
  read?: boolean;
  created_at?: string;
  updated_at?: string;
  redirect_url?: string;
  redirect_url_web?: string;
}

const LIMIT = 10;
type TabType = "all" | "unread";

interface NotificationContext {
  unreadCount: number;
  checkedValues: CheckboxValueType[];
  setCheckedValues: React.Dispatch<React.SetStateAction<CheckboxValueType[]>>;
  onMarkAllAsRead: () => Promise<void>;
  onRead: (id: string) => void;
  isLoading: boolean;
  notifications: NotificationModel[];
  hasMore: number;
  tab: TabType;
  setTab: React.Dispatch<React.SetStateAction<TabType>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  mutateNotificationList: () => void;
  isCallMutate: boolean;
  setIsCallMutate: React.Dispatch<React.SetStateAction<boolean>>;
}

const [Provider, useNotificationContext] = createContext<NotificationContext>();

export const NotificationProvider = ({ children }: { children: ReactElement }) => {
  const { authState } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);
  const [checkedValues, setCheckedValues] = useState<CheckboxValueType[]>([]);
  const [isCallMutate, setIsCallMutate] = useState(false);
  const [hasMore, setHasMore] = useState(0);
  const [tab, setTab] = useState<TabType>("all");
  const [page, setPage] = useState<number>(1);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [tab]);

  // Query for notifications
  const {
    data: notificationResponse,
    isLoading,
    refetch: mutateNotificationList,
  } = useAxiosQuery<{
    data: {
      total: number;
      notifications: NotificationModel[];
    };
  }>({
    url: "/v1/notification",
    method: "get",
    params: {
      read: tab === "unread" ? false : undefined,
      limit: LIMIT,
      page: page,
    },
    includeParamsInQueryKey: true,
    enabled: authState === "logged",
  });

  // Update hasMore when notification data changes
  useEffect(() => {
    if (notificationResponse?.data) {
      const total = notificationResponse.data.total;
      setHasMore(total > page * LIMIT ? total - page * LIMIT : 0);
    }
  }, [notificationResponse?.data, page]);

  // Query for unread count
  const { refetch: reloadUnread } = useAxiosQuery<{ data: IStatisticNotification }>({
    url: "/v1/notification/statistic/unread-count",
    method: "get",
    enabled: authState === "logged",
  });

  // Update unread count periodically
  useEffect(() => {
    const updateUnreadCount = async () => {
      if (authState === "logged") {
        const result = await reloadUnread();
        if (result?.data?.data) {
          const data = result.data.data;
          setUnreadCount(
            data.activities +
              data.market_movement +
              data.portfolio +
              data.system +
              data.news_feed +
              data.signal +
              data.alert +
              data.news,
          );
        }
      }
    };

    // Initial load
    updateUnreadCount();

    // Set up interval
    const intervalId = setInterval(updateUnreadCount, 30000);
    return () => clearInterval(intervalId);
  }, [authState, reloadUnread]);

  // Memoized notifications list
  const notifications = useMemo<NotificationModel[]>(
    () => notificationResponse?.data?.notifications || [],
    [notificationResponse?.data?.notifications],
  );

  // Mutation for marking a notification as read
  const { mutateAsync: triggerRead } = useMyMutation(
    {
      url: "/v1/notification",
      method: "post",
    },
    {
      async onSuccess() {
        await reloadUnread();
        await mutateNotificationList();
      },
      onError(error) {
        console.error("Error marking notification as read:", error);
      },
    },
  );

  // Mutation for marking all notifications as read
  const { mutateAsync: triggerMarkAllRead } = useMyMutation(
    {
      url: "v1/notification/mark-all-as-read",
      method: "post",
    },
    {
      async onSuccess() {
        await reloadUnread();
        await mutateNotificationList();
        setIsCallMutate(true);
      },
      onError(error) {
        console.error("Error marking all notifications as read:", error);
      },
    },
  );

  // Handler for reading a notification
  const onRead = useCallback(
    (id: string) => {
      triggerRead({ ids: [id] });
    },
    [triggerRead],
  );

  // Handler for marking all notifications as read
  const onMarkAllAsRead = useCallback(async () => {
    await triggerMarkAllRead({});
  }, [triggerMarkAllRead]);

  return (
    <Provider
      value={{
        unreadCount,
        onMarkAllAsRead,
        onRead,
        checkedValues,
        setCheckedValues,
        isLoading,
        notifications,
        tab,
        setTab,
        setPage,
        hasMore,
        mutateNotificationList,
        isCallMutate,
        setIsCallMutate,
      }}
    >
      {children}
    </Provider>
  );
};

export { useNotificationContext };
export default NotificationProvider;
